import { PDFDocument, StandardFonts } from 'pdf-lib';
import { searchCarriers } from '../../api/figApi';

const BLANK_PDF_URL = '/acord/ACORD_28_blank.pdf';

/**
 * Generate a filled ACORD 28 — Evidence of Commercial Property Insurance.
 * Uses pdf-lib to fill the blank PDF form fields.
 */
export default async function generateACORD28(formData, insured, getToken) {
  const res = await fetch(BLANK_PDF_URL);
  if (!res.ok) throw new Error(`Failed to load blank ACORD 28 (${res.status})`);
  const pdfBytes = await res.arrayBuffer();

  const pdfDoc = await PDFDocument.load(pdfBytes);
  const form = pdfDoc.getForm();
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  // Log ALL field names for mapping
  const allFields = form.getFields();
  console.log(`[ACORD 28] Total form fields: ${allFields.length}`);
  console.log('[ACORD 28] All field names:');
  allFields.forEach(f => console.log(`  ${f.constructor.name}: "${f.getName()}"`));

  // --- Helpers ---
  function setText(name, value) {
    if (!value && value !== 0) return;
    try {
      const field = form.getTextField(name);
      field.setText(String(value));
      try { field.updateAppearances(font); } catch (ae) { console.warn(`[ACORD 28] Appearance failed for "${name}":`, ae.message); }
    } catch (err) {
      console.warn(`[ACORD 28] Text field not found: "${name}"`, err.message);
    }
  }

  function setCheck(name) {
    try {
      const field = form.getCheckBox(name);
      field.check();
    } catch (err) {
      console.warn(`[ACORD 28] Checkbox not found: "${name}"`, err.message);
    }
  }

  const f = formData;
  /**
   * Money formatter. A missing value (null / undefined / '') is "not entered"
   * and renders blank; 0 is a real amount and renders as $0. Truthiness must
   * not be used as the null check — that made a genuine zero deductible
   * indistinguishable from one that was never filled in.
   */
  const money = (v, { symbol = true } = {}) => {
    if (v === null || v === undefined || v === '') return '';
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);   // e.g. "TBD" — pass through rather than render NaN
    return (symbol ? '$' : '') + n.toLocaleString();
  };
  const fmtNum = (v) => money(v);
  // AMOUNT OF INSURANCE is the one cell with a "$" preprinted on the form
  // (page 1, x=250 y=436, immediately left of the field), so its value is
  // rendered without a symbol to avoid "$ $50,000,000".
  const fmtNumNoSymbol = (v) => money(v, { symbol: false });
  const fmtDate = (d) => {
    if (!d) return '';
    // Convert YYYY-MM-DD to MM/DD/YYYY
    const parts = String(d).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (parts) return `${parts[2]}/${parts[3]}/${parts[1]}`;
    return d; // already in other format
  };

  // --- Header / Date ---
  setText('Form_CompletionDate_A', fmtDate(f.date));

  // --- Producer ---
  setText('Producer_FullName_A', 'Fragos Insurance Group LLC');
  setText('Producer_ContactPerson_FullName_A', 'Marc Fragos');
  setText('Producer_MailingAddress_LineOne_A', '16915 Walden Rd Suite #312');
  setText('Producer_MailingAddress_CityName_A', 'Montgomery');
  setText('Producer_MailingAddress_StateOrProvinceCode_A', 'TX');
  setText('Producer_MailingAddress_PostalCode_A', '77356');
  setText('Producer_ContactPerson_PhoneNumber_A', '(936) 755-2521');
  setText('Producer_FaxNumber_A', '');
  setText('Producer_ContactPerson_EmailAddress_A', 'marc@myfiginsurance.com');

  // --- Company / Policy ---
  setText('Insurer_FullName_A', f.carrier);
  setText('Insurer_NAICCode_A', f.naicCode);

  // Look up carrier address from FIG_Carriers using NAIC code
  if (getToken && f.naicCode) {
    try {
      const token = await getToken();
      // searchCarriers caches the full list — search by NAIC code in CompanyName won't work,
      // but the cached list has NAICCode mapped from Title field
      const results = await searchCarriers(token, f.carrier || '');
      const carrierMatch = results.find(c => String(c.NAICCode) === String(f.naicCode));
      if (carrierMatch) {
        setText('Insurer_MailingAddress_AddressLineOne_A', carrierMatch.Address);
        setText('Insurer_MailingAddress_CityName_A', carrierMatch.City);
        setText('Insurer_MailingAddress_StateOrProvinceCode_A', carrierMatch.State);
        setText('Insurer_MailingAddress_PostalCode_A', carrierMatch.Zip);
      }
    } catch (err) {
      console.warn('[ACORD 28] Carrier address lookup failed:', err.message);
    }
  }
  setText('Policy_TypeDescription_A', f.policyType);
  setText('Policy_PolicyNumberIdentifier_A', f.policyNumber);
  setText('Policy_EffectiveDate_A', f.effectiveDate);
  setText('Policy_ExpirationDate_A', f.expirationDate);
  setText('AdditionalInterest_AccountNumberIdentifier_A', f.loanNumber);

  // --- Named Insured ---
  setText('NamedInsured_FullName_A', insured?.CompanyName || '');
  setText('NamedInsured_MailingAddress_LineOne_A', insured?.MailingAddress || '');
  setText('NamedInsured_MailingAddress_CityName_A', insured?.MailingCity || '');
  setText('NamedInsured_MailingAddress_StateOrProvinceCode_A', insured?.MailingState || '');
  setText('NamedInsured_MailingAddress_PostalCode_A', insured?.MailingZip || '');
  setText('NamedInsured_FullName_B', f.additionalNamedInsured);

  // --- Property Information ---
  if (f.coverBuilding) setCheck('Property_Building_CoverageIndicator_A');
  if (f.coverBPP) setCheck('Property_BusinessPersonalProperty_CoverageIndicator_A');
  setText('Location_PhysicalAddress_LineOne_A', f.locAddress);
  setText('Location_PhysicalAddress_LineTwo_A', f.locAddress2);
  setText('Location_PhysicalAddress_CityName_A', f.locCity);
  setText('Location_PhysicalAddress_StateOrProvinceCode_A', f.locState);
  setText('Location_PhysicalAddress_PostalCode_A', f.locZip);
  setText('Location_LocationDescription_A', f.buildingDescription);

  // --- Coverage Information ---
  // Perils
  if (f.perilsInsured === 'Basic') setCheck('Policy_PolicyType_BasicIndicator_A');
  if (f.perilsInsured === 'Broad') setCheck('Policy_PolicyType_BroadIndicator_A');
  if (f.perilsInsured === 'Special') setCheck('Policy_PolicyType_SpecialIndicator_A');

  // Amounts
  setText('CommercialPropertyCoverage_Property_LimitAmount_A', fmtNumNoSymbol(f.amountOfInsurance));
  setText('CommercialPropertyCoverage_Property_DeductibleAmount_A', fmtNum(f.deductible));

  // Business Income / Rental Value
  if (f.businessIncome) setCheck('CommercialPropertyCoverage_BusinessIncomeOrRentalValue_BusinessIncomeCoverageIndicator_A');
  if (f.rentalValue) setCheck('CommercialPropertyCoverage_BusinessIncomeOrRentalValue_RentalValueCoverageIndicator_A');

  if (f.businessIncome || f.rentalValue) {
    setCheck('CommercialPropertyCoverage_BusinessIncomeOrRentalValue_YesIndicator_A');
    setText('CommercialPropertyCoverage_BusinessIncomeOrRentalValue_LimitAmount_A', fmtNum(f.businessIncomeLimit || f.rentalValueLimit));
    if (f.businessIncomeType === 'Actual Loss Sustained') {
      setCheck('CommercialPropertyCoverage_BusinessIncomeOrRentalValue_ActualLossSustainedIndicator_A');
    } else {
      setText('CommercialPropertyCoverage_BusinessIncomeOrRentalValue_ActualLossSustainedMonthCount_A', f.businessIncomeType);
    }
  } else {
    setCheck('CommercialPropertyCoverage_BusinessIncomeOrRentalValue_NoIndicator_A');
  }

  // --- Yes/No/N/A coverage fields ---
  // Some peril fields store underwriting vocab ("Included"/"Excluded") instead of
  // "Yes"/"No". Normalize so the YES/NO/NA indicator boxes render correctly.
  const ynnVal = (v) => (v === 'Included' ? 'Yes' : v === 'Excluded' ? 'No' : v);
  const ynnFields = [
    { key: 'blanketCoverage', yes: 'CommercialProperty_Blanket_YesIndicator_A', no: 'CommercialProperty_Blanket_NoIndicator_A', na: 'CommercialProperty_Blanket_NotApplicableIndicator_A' },
    { key: 'terrorismCoverage', yes: 'CommercialPropertyCoverage_Terrorism_YesIndicator_A', no: 'CommercialPropertyCoverage_Terrorism_NoIndicator_A', na: 'CommercialPropertyCoverage_Terrorism_NotApplicableIndicator_A' },
    { key: 'terrorismExclusion', yes: 'CommercialPropertyCoverage_TerrorismExclusion_YesIndicator_A', no: 'CommercialPropertyCoverage_TerrorismExclusion_NoIndicator_A', na: 'CommercialPropertyCoverage_TerrorismExclusion_NotApplicableIndicator_A' },
    { key: 'domesticTerrorismExcluded', yes: 'CommercialPropertyCoverage_DomesticTerrorismExclusion_YesIndicator_A', no: 'CommercialPropertyCoverage_DomesticTerrorismExclusion_NoIndicator_A', na: 'CommercialPropertyCoverage_DomesticTerrorismExclusion_NotApplicableIndicator_A' },
    { key: 'fungusCoverage', yes: 'CommercialPropertyCoverage_LimitedFungus_YesIndicator_A', no: 'CommercialPropertyCoverage_LimitedFungus_NoIndicator_A', na: 'CommercialPropertyCoverage_LimitedFungus_NotApplicableIndicator_A' },
    { key: 'fungusExclusion', yes: 'CommercialPropertyCoverage_FungusExclusion_YesIndicator_A', no: 'CommercialPropertyCoverage_FungusExclusion_NoIndicator_A', na: 'CommercialPropertyCoverage_FungusExclusion_NotApplicableIndicator_A' },
    { key: 'replacementCost', yes: 'CommercialPropertyCoverage_ReplacementCost_YesIndicator_A', no: 'CommercialPropertyCoverage_ReplacementCost_NoIndicator_A', na: 'CommercialPropertyCoverage_ReplacementCost_NotApplicableIndicator_A' },
    { key: 'agreedValue', yes: 'CommercialPropertyCoverage_AgreedValue_YesIndicator_A', no: 'CommercialPropertyCoverage_AgreedValue_NoIndicator_A', na: 'CommercialPropertyCoverage_AgreedValue_NotApplicableIndicator_A' },
    { key: 'coinsurance', yes: 'CommercialPropertyCoverage_Coinsurance_YesIndicator_A', no: 'CommercialPropertyCoverage_Coinsurance_NoIndicator_A', na: 'CommercialPropertyCoverage_Coinsurance_NotApplicableIndicator_A' },
    { key: 'equipmentBreakdown', yes: 'CommercialPropertyCoverage_EquipmentBreakdown_YesIndicator_A', no: 'CommercialPropertyCoverage_EquipmentBreakdown_NoIndicator_A', na: 'CommercialPropertyCoverage_EquipmentBreakdown_NotApplicableIndicator_A' },
    { key: 'earthMovement', yes: 'CommercialPropertyCoverage_EarthMovement_YesIndicator_A', no: 'CommercialPropertyCoverage_EarthMovement_NoIndicator_A', na: 'CommercialPropertyCoverage_EarthMovement_NotApplicableIndicator_A' },
    { key: 'flood', yes: 'CommercialPropertyCoverage_Flood_YesIndicator_A', no: 'CommercialPropertyCoverage_Flood_NoIndicator_A', na: 'CommercialPropertyCoverage_Flood_NotApplicableIndicator_A' },
    { key: 'permissionToWaiveSubrogation', yes: 'CommercialProperty_SubrogationWaiver_YesIndicator_A', no: 'CommercialProperty_SubrogationWaiver_NoIndicator_A', na: 'CommercialProperty_SubrogationWaiver_NotApplicableIndicator_A' },
  ];

  for (const { key, yes, no, na } of ynnFields) {
    const val = ynnVal(f[key]);
    if (val === 'Yes') setCheck(yes);
    else if (val === 'No') setCheck(no);
    else if (val === 'N/A') setCheck(na);
  }

  // Fungus limit/ded
  setText('CommercialPropertyCoverage_LimitedFungus_LimitAmount_A', fmtNum(f.fungusLimit));
  setText('CommercialPropertyCoverage_LimitedFungus_DeductibleAmount_A', fmtNum(f.fungusDeductible));

  // Coinsurance %
  if (f.coinsurance === 'Yes' && f.coinsurancePct) {
    setText('CommercialProperty_Premises_CoinsurancePercent_A', f.coinsurancePct);
  }

  // Equipment Breakdown limit/ded
  setText('CommercialPropertyCoverage_EquipmentBreakdown_LimitAmount_A', fmtNum(f.equipmentBreakdownLimit));
  setText('CommercialPropertyCoverage_EquipmentBreakdown_DeductibleAmount_A', fmtNum(f.equipmentBreakdownDed));

  // Ordinance or Law A — Undamaged Portion of Building
  const olA = f.ordinanceOrLaw1;
  if (olA === 'Yes') setCheck('CommercialProperty_BuildingOrdinanceOrLaw_UndamagedPortionOfBuildingYesIndicator_A');
  else if (olA === 'No') setCheck('CommercialProperty_BuildingOrdinanceOrLaw_UndamagedPortionOfBuildingNoIndicator_A');
  else if (olA === 'N/A') setCheck('CommercialProperty_BuildingOrdinanceOrLaw_UndamagedPortionOfBuildingNotApplicableIndicator_A');
  setText('CommercialPropertyCoverage_BuildingOrdinanceOrLaw_UndamagedPortionBuildingLimitAmount_A', fmtNum(f.ordinanceOrLaw1Limit));
  setText('CommercialPropertyCoverage_BuildingOrdinanceOrLaw_UndamagedPortionBuildingDeductibleAmount_A', fmtNum(f.ordinanceOrLaw1Ded));

  // Ordinance or Law B — Demolition Costs
  const olB = f.ordinanceOrLaw2;
  if (olB === 'Yes') setCheck('CommercialProperty_BuildingOrdinanceOrLaw_DemolitionCostsYesIndicator_A');
  else if (olB === 'No') setCheck('CommercialProperty_BuildingOrdinanceOrLaw_DemolitionCostsNoIndicator_A');
  else if (olB === 'N/A') setCheck('CommercialProperty_BuildingOrdinanceOrLaw_DemolitionCostsNotApplicableIndicator_A');
  setText('CommercialProperty_BuildingOrdinanceOrLaw_DemolitionCostsLimitAmount_A', fmtNum(f.ordinanceOrLaw2Limit));
  setText('CommercialProperty_BuildingOrdinanceOrLaw_DemolitionCostsDeductibleAmount_A', fmtNum(f.ordinanceOrLaw2Ded));

  // Ordinance or Law C — Increased Cost of Construction
  const olC = f.ordinanceOrLaw3;
  if (olC === 'Yes') setCheck('CommercialProperty_BuildingOrdinanceOrLaw_IncreasedCostOfConstructionYesIndicator_A');
  else if (olC === 'No') setCheck('CommercialProperty_BuildingOrdinanceOrLaw_IncreasedCostOfConstructionNoIndicator_A');
  else if (olC === 'N/A') setCheck('CommercialProperty_BuildingOrdinanceOrLaw_IncreasedCostOfConstructionNotApplicableIndicator_A');
  setText('CommercialProperty_BuildingOrdinanceOrLaw_IncreasedCostOfConstructionLimitAmount_A', fmtNum(f.ordinanceOrLaw3Limit));
  setText('CommercialProperty_BuildingOrdinanceOrLaw_IncreasedCostOfConstructionDeductibleAmount_A', fmtNum(f.ordinanceOrLaw3Ded));

  // Deductible printer: Desc (text) wins; otherwise format the numeric field as $25,000.
  // fmtNum now carries the "$" itself, so it must not be prefixed again here.
  // Dropping the truthiness check also lets a genuine 0 render as $0.
  const fmtDed = (desc, num) => desc || fmtNum(num);

  // Earth Movement limit/ded
  setText('CommercialPropertyCoverage_EarthMovement_LimitAmount_A', fmtNum(f.earthMovementLimit));
  setText('CommercialPropertyCoverage_EarthMovement_DeductibleAmount_A', fmtDed(f.earthMovementDedDesc, f.earthMovementDed));

  // Flood limit/ded
  setText('CommercialPropertyCoverage_Flood_LimitAmount_A', fmtNum(f.floodLimit));
  setText('CommercialPropertyCoverage_Flood_DeductibleAmount_A', fmtDed(f.floodDedDesc, f.floodDed));

  // Wind/Hail
  const windHailVal = ynnVal(f.windHail);
  if (windHailVal === 'Yes') setCheck('CommercialPropertyCoverage_WindHail_YesIndicator_A');
  if (windHailVal === 'No') setCheck('CommercialPropertyCoverage_WindHail_NoIndicator_A');
  if (f.windHailDiffProvisions) {
    setCheck('CommercialPropertyCoverage_WindHail_SubjectToProvisionsIndicator_A');
  } else {
    setCheck('CommercialPropertyCoverage_WindHail_SubjectToProvisionsNoIndicator_A');
  }
  setText('CommercialPropertyCoverage_WindHail_LimitAmount_A', fmtNum(f.windHailLimit));
  setText('CommercialPropertyCoverage_WindHail_DeductibleAmount_A', fmtDed(f.windHailDedDesc, f.windHailDed));

  // Named Storm
  const namedStormVal = ynnVal(f.namedStorm);
  if (namedStormVal === 'Yes') setCheck('CommercialPropertyCoverage_NamedWindstorm_YesIndicator_A');
  if (namedStormVal === 'No') setCheck('CommercialPropertyCoverage_NamedWindstorm_NoIndicator_A');
  if (f.namedStormDiffProvisions) {
    setCheck('CommercialPropertyCoverage_NamedWindstorm_SubjectToProvisionsIndicator_A');
  } else {
    setCheck('CommercialPropertyCoverage_NamedWindstorm_SubjectToProvisionsNoIndicator_A');
  }
  setText('CommercialPropertyCoverage_NamedWindstorm_LimitAmount_A', fmtNum(f.namedStormLimit));
  setText('CommercialPropertyCoverage_NamedWindstorm_DeductibleAmount_A', fmtDed(f.namedStormDedDesc, f.namedStormDed));

  // --- Additional Interest ---
  console.log('[ACORD 28 PDF] All interest form fields:', Object.entries(f).filter(([k]) => k.startsWith('interest')).map(([k, v]) => `${k}="${v}"`));
  console.log('[ACORD 28 PDF] O&L values:', {
    olA: f.ordinanceOrLaw1, olALimit: f.ordinanceOrLaw1Limit, olADed: f.ordinanceOrLaw1Ded,
    olB: f.ordinanceOrLaw2, olBLimit: f.ordinanceOrLaw2Limit, olBDed: f.ordinanceOrLaw2Ded,
    olC: f.ordinanceOrLaw3, olCLimit: f.ordinanceOrLaw3Limit, olCDed: f.ordinanceOrLaw3Ded,
  });
  // Each indicator is set from membership in the Designations array, so a
  // single interest holder can carry more than one designation (a lender is
  // routinely both Mortgagee and Lender's Loss Payable).
  const designations = Array.isArray(f.designations) ? f.designations : [];
  if (designations.length === 0) {
    console.warn(
      `[ACORD 28] ${insured?.CompanyName || 'insured'}: no additional-interest designation — ` +
      'no matching interest record, so CONTRACT OF SALE / MORTGAGEE / ' +
      "LENDER'S LOSS PAYABLE / LOSS PAYEE are all left unchecked."
    );
  }
  const DESIGNATION_INDICATORS = {
    'Contract of Sale': 'AdditionalInterest_Interest_ContractOfSaleIndicator_A',
    "Lender's Loss Payable": 'AdditionalInterest_Interest_LendersLossPayableIndicator_A',
    'Loss Payee': 'AdditionalInterest_Interest_LossPayeeIndicator_A',
    Mortgagee: 'AdditionalInterest_Interest_MortgageeIndicator_A',
  };
  for (const d of designations) {
    const field = DESIGNATION_INDICATORS[d];
    if (field) setCheck(field);
    else console.warn(`[ACORD 28] unrecognised designation "${d}" — no box on the form for it.`);
  }

  console.log('[ACORD 28 PDF] Interest data:', { name: f.interestName, addr: f.interestAddress, city: f.interestCity, type: f.interestType });
  setText('AdditionalInterest_FullName_A', f.interestName);
  setText('AdditionalInterest_MailingAddress_LineOne_A', f.interestAddress);
  setText('AdditionalInterest_MailingAddress_CityName_A', f.interestCity);
  setText('AdditionalInterest_MailingAddress_StateOrProvinceCode_A', f.interestState);
  setText('AdditionalInterest_MailingAddress_PostalCode_A', f.interestZip);

  // Second additional interest
  setText('AdditionalInterest_FullName_B', f.interest2Name);
  setText('AdditionalInterest_MailingAddress_LineOne_B', f.interest2Address);
  setText('AdditionalInterest_MailingAddress_CityName_B', f.interest2City);
  setText('AdditionalInterest_MailingAddress_StateOrProvinceCode_B', f.interest2State);
  setText('AdditionalInterest_MailingAddress_PostalCode_B', f.interest2Zip);

  // Signature
  setText('Producer_AuthorizedRepresentative_Signature_A', 'Marc Fragos');

  // --- Save and open in new tab ---
  const filledBytes = await pdfDoc.save({ updateFieldAppearances: false });
  const blob = new Blob([filledBytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
}
